/**
 * Asignar una habitación a un inquilino.
 *
 * SUSTITUYE AL ASISTENTE DE 6 PASOS. La aplicación ya no redacta contratos: el contrato existe
 * fuera, en papel o en un PDF firmado, y aquí solo se registra a quién se le ha dado qué
 * habitación, desde cuándo y por cuánto. Eso cabe en una pantalla.
 *
 * POR DEBAJO SIGUE CREANDO UN ARRENDAMIENTO, y no es un tecnicismo: el vínculo inquilino ↔
 * habitación ES el arrendamiento. De él salen los cobros mensuales, la ocupación que se ve en el
 * panel y lo que el inquilino ve en su portal. Quitar el modelo habría vaciado tres pantallas;
 * lo que sobraba era la ceremonia de darlo de alta, no el dato.
 *
 * Y SE ACTIVA EN EL MISMO PASO (`activate: true`). Poner a alguien en una habitación es que ya
 * vive ahí: dejarlo en borrador hacía que la ficha siguiera diciendo «sin habitación» y había
 * que encontrar un segundo botón. Activar emite sus cobros desde la fecha de inicio.
 *
 * SE ENTRA POR EL INQUILINO. El asistente empezaba por la habitación y buscaba a la persona
 * después; se usa al revés —primero das de alta a quien llega, luego le colocas—, así que el
 * inquilino viene dado y lo único que hay que elegir es dónde va.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, api, queryKeys, type AvailableUnit } from '../../api/client';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Drawer } from '../../components/overlay/Drawer';
import {
  Field,
  FormRow,
  FormSection,
  Input,
  MoneyInput,
  Select,
  Textarea,
} from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { useLeaseInvalidation } from '../leases/LeaseActions';
import { colocacion } from './calendario';
import styles from './AssignRoomDrawer.module.css';

interface AssignRoomDrawerProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
}

interface FormState {
  unitId: string;
  startDate: string;
  endDate: string;
  monthlyRent: string;
  depositAmount: string;
  /** Adelanto mensual de suministros: se regulariza cada cuatrimestre en Suministros. */
  utilityAdvance: string;
  paymentDay: string;
  noticePeriodDays: string;
  indexationType: 'ninguna' | 'ipc' | 'fija';
  indexationValue: string;
  signedAt: string;
  notes: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Una estancia que ya ha empezado emite al asignarla los cobros que van desde entonces. */
export const YA_EMPEZADA = 'Ya ha empezado: al asignarla se emiten sus cobros desde esta fecha hasta este mes.';

function oneYearFrom(iso: string): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCFullYear(date.getUTCFullYear() + 1);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

function initialState(): FormState {
  const start = todayIso();
  return {
    unitId: '',
    startDate: start,
    endDate: oneYearFrom(start),
    monthlyRent: '',
    depositAmount: '',
    utilityAdvance: '50.00',
    paymentDay: '1',
    noticePeriodDays: '30',
    indexationType: 'ipc',
    indexationValue: '2.1',
    signedAt: '',
    notes: '',
  };
}

export function AssignRoomDrawer({ open, onClose, tenantId, tenantName }: AssignRoomDrawerProps) {
  const [form, setForm] = useState<FormState>(initialState);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);
  const invalidate = useLeaseInvalidation();

  /*
   * Se piden TODAS, libres y ocupadas. Enseñar solo las libres deja al usuario buscando una
   * habitación que no aparece sin poder saber por qué; enseñarla ocupada, con quién la tiene y
   * hasta cuándo, contesta la pregunta antes de que la haga.
   */
  const unitsQuery = useQuery({
    queryKey: queryKeys.availableUnits({ all: true }),
    queryFn: () => api.availableUnits({ includeOccupied: true }),
    enabled: open,
  });

  const units = unitsQuery.data?.data ?? [];
  const selected = units.find((unit) => unit.id === form.unitId);

  useEffect(() => {
    if (!open) {
      setForm(initialState());
      setFieldErrors({});
      setTouched(false);
    }
  }, [open]);

  /* La renta y la fianza se prerrellenan con la de referencia de la habitación, pero solo
     mientras el usuario no haya escrito lo suyo. */
  useEffect(() => {
    if (selected && !touched) {
      setForm((current) => ({
        ...current,
        monthlyRent: selected.baseRent,
        depositAmount: selected.baseRent,
      }));
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function patch(next: Partial<FormState>) {
    setForm((current) => ({ ...current, ...next }));
  }

  /**
   * Cada opción lleva el inmueble delante: «Hab. 2» a secas no dice dónde está, y con quince
   * habitaciones repartidas en cuatro pisos hay varias que se llaman igual.
   */
  const options = useMemo(
    () =>
      [...units]
        .sort(
          (a, b) =>
            a.property.name.localeCompare(b.property.name, 'es') ||
            a.code.localeCompare(b.code, 'es', { numeric: true }),
        )
        .map((unit: AvailableUnit) => ({
          value: unit.id,
          label: `${unit.property.name} · ${unit.code}${unit.floor ? ` (${unit.floor})` : ''}${
            unit.occupiedBy ? ' — ocupada' : ''
          }`,
        })),
    [units],
  );

  const create = useMutation({
    mutationFn: () =>
      api.createLease({
        unitId: form.unitId,
        startDate: form.startDate,
        endDate: form.endDate || null,
        monthlyRent: form.monthlyRent,
        depositAmount: form.depositAmount || '0',
        utilityAdvance: form.utilityAdvance || '0',
        paymentDay: Number(form.paymentDay) || 1,
        noticePeriodDays: form.noticePeriodDays ? Number(form.noticePeriodDays) : null,
        indexationType: form.indexationType,
        indexationValue:
          form.indexationType === 'ninguna' ? null : Number(form.indexationValue) || 0,
        signedAt: form.signedAt || null,
        notes: form.notes.trim() || null,
        tenants: [{ tenantId, isPrimary: true }],
        activate: true,
      }),
    onSuccess: () => {
      invalidate();
      toast.success(`${tenantName.split(' ')[0]} ya tiene habitación asignada.`);
      onClose();
    },
    onError: (error: unknown) => {
      if (!(error instanceof ApiError)) {
        toast.error('No se ha podido asignar la habitación.');
        return;
      }
      // El error del servidor se pinta EN EL CAMPO, que es donde se puede arreglar.
      const errors: Record<string, string> = {};
      for (const detail of error.fieldErrors) errors[detail.path] = detail.message;
      setFieldErrors(errors);
      if (Object.keys(errors).length === 0) toast.error(error.message);
    },
  });

  const valid = Boolean(form.unitId && form.startDate && form.monthlyRent);

  return (
    <Drawer
      open={open}
      title="Asignar habitación"
      subtitle={tenantName}
      onClose={onClose}
      dirty={Boolean(form.unitId)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => create.mutate()} disabled={!valid || create.isPending}>
            {create.isPending ? 'Asignando…' : 'Asignar habitación'}
          </Button>
        </>
      }
    >
      <FormSection
        title="Habitación"
        hint="De aquí salen los cobros mensuales y la ocupación que se ve en el panel."
      >
        <Field label="Dónde va a vivir" required error={fieldErrors.unitId}>
          <Select
            options={options}
            placeholder="Elige una habitación…"
            value={form.unitId}
            onChange={(event) => patch({ unitId: event.target.value })}
            invalid={Boolean(fieldErrors.unitId)}
          />
        </Field>

        {unitsQuery.isSuccess && units.length === 0 && (
          <div className={styles.warning}>
            <Icon name="info" size={15} />
            <span>
              Todavía no hay habitaciones. Créalas desde la pestaña «Habitaciones» de un
              inmueble: sin ellas no hay nada que asignar.
            </span>
          </div>
        )}

        {/*
          La habitación ocupada NO se bloquea: se mira el CALENDARIO. Una estancia que no se pisa
          con la de quien vive ahí cabe —antes o después—, y el servidor tiene la última palabra.
        */}
        {selected?.occupiedBy && (
          <div className={styles.warning}>
            <Icon name="info" size={15} />
            <span>{colocacion(selected.occupiedBy, form.startDate, form.endDate, todayIso()).texto}</span>
          </div>
        )}
      </FormSection>

      <FormSection title="Estancia">
        <FormRow>
          <Field
            label="Desde"
            required
            error={fieldErrors.startDate}
            help={form.startDate && form.startDate < todayIso() ? YA_EMPEZADA : undefined}
          >
            <Input
              type="date"
              value={form.startDate}
              onChange={(event) => patch({ startDate: event.target.value })}
              invalid={Boolean(fieldErrors.startDate)}
            />
          </Field>
          <Field label="Hasta" optional help="En blanco, indefinida." error={fieldErrors.endDate}>
            <Input
              type="date"
              value={form.endDate}
              onChange={(event) => patch({ endDate: event.target.value })}
              invalid={Boolean(fieldErrors.endDate)}
            />
          </Field>
        </FormRow>

        <FormRow>
          <Field label="Renta mensual" required error={fieldErrors.monthlyRent}>
            <MoneyInput
              value={form.monthlyRent}
              onValueChange={(value) => {
                setTouched(true);
                patch({ monthlyRent: value });
              }}
              onChange={() => undefined}
              invalid={Boolean(fieldErrors.monthlyRent)}
            />
          </Field>
          <Field label="Fianza" optional help="Normalmente una mensualidad.">
            <MoneyInput
              value={form.depositAmount}
              onValueChange={(value) => {
                setTouched(true);
                patch({ depositAmount: value });
              }}
              onChange={() => undefined}
            />
          </Field>
          <Field
            label="Suministros al mes"
            optional
            error={fieldErrors.utilityAdvance}
            help="Lo que adelanta para luz, agua, internet… Se regulariza cada cuatrimestre."
          >
            <MoneyInput
              value={form.utilityAdvance}
              onValueChange={(value) => patch({ utilityAdvance: value })}
              onChange={() => undefined}
              invalid={Boolean(fieldErrors.utilityAdvance)}
            />
          </Field>
          <Field
            label="Día de pago"
            optional
            error={fieldErrors.paymentDay}
            help="Del 1 al 28: los días 29-31 no existen en todos los meses."
          >
            <Input
              value={form.paymentDay}
              onChange={(event) => patch({ paymentDay: event.target.value })}
              numeric
              inputMode="numeric"
              min={1}
              max={28}
              invalid={Boolean(fieldErrors.paymentDay)}
            />
          </Field>
        </FormRow>
      </FormSection>

      <FormSection
        title="Condiciones"
        hint="Lo que dice el contrato que ya tienes firmado. Se puede cambiar después."
      >
        <FormRow>
          <Field label="Firmado el" optional error={fieldErrors.signedAt}>
            <Input
              type="date"
              value={form.signedAt}
              onChange={(event) => patch({ signedAt: event.target.value })}
              invalid={Boolean(fieldErrors.signedAt)}
            />
          </Field>
          <Field label="Preaviso" optional help="En días. Alimenta la alerta de vencimiento.">
            <Input
              value={form.noticePeriodDays}
              onChange={(event) => patch({ noticePeriodDays: event.target.value })}
              numeric
              inputMode="numeric"
              suffix="días"
            />
          </Field>
        </FormRow>

        <FormRow>
          <Field label="Actualización de renta" optional>
            <Select
              options={[
                { value: 'ipc', label: 'IPC' },
                { value: 'fija', label: 'Porcentaje fijo' },
                { value: 'ninguna', label: 'No se actualiza' },
              ]}
              value={form.indexationType}
              onChange={(event) =>
                patch({ indexationType: event.target.value as FormState['indexationType'] })
              }
            />
          </Field>
          {form.indexationType !== 'ninguna' && (
            <Field label="Porcentaje" optional error={fieldErrors.indexationValue}>
              <Input
                value={form.indexationValue}
                onChange={(event) => patch({ indexationValue: event.target.value })}
                numeric
                inputMode="decimal"
                suffix="%"
                invalid={Boolean(fieldErrors.indexationValue)}
              />
            </Field>
          )}
        </FormRow>

        <Field label="Notas" optional>
          <Textarea
            value={form.notes}
            onChange={(event) => patch({ notes: event.target.value })}
            placeholder="Lo que convenga recordar de esta estancia."
            rows={3}
          />
        </Field>
      </FormSection>
    </Drawer>
  );
}
